import React from 'react';
import Select, {
  components,
  OptionProps,
  MenuProps,
  MultiValueGenericProps,
  GroupBase,
  OptionsOrGroups,
  Props as SelectProps,
} from 'react-select';
import { StylishNewCheckbox } from './StylishNewCheckbox';
import classNames from 'classnames';

export type OptionType = {
  label: string | React.ReactNode;
  value: string | boolean;
};

type MenuComponentProps = MenuProps<OptionType> & {
  value?: OptionType | OptionType[] | null;
  onChange?: (value: OptionType | OptionType[]) => void;
  isMulti: boolean;
  options: OptionsOrGroups<OptionType, GroupBase<OptionType>>;
};

const flattenOptions = (
  options: OptionsOrGroups<OptionType, GroupBase<OptionType>>
): OptionType[] => {
  return options.flatMap((item) => ('options' in item ? item.options : [item]));
};

const Menu = ({
  value,
  onChange,
  isMulti,
  options,
  ...props
}: MenuComponentProps) => {
  const flatOptions = flattenOptions(options);
  const isAllSelected =
    value != null &&
    Array.isArray(value) &&
    value.length === flatOptions.length &&
    flatOptions.every((option: OptionType) =>
      value.some((selected) => selected.value === option.value)
    );

  const handleSelectAll = () => {
    if (isMulti && onChange) {
      if (isAllSelected) {
        onChange([]);
      } else {
        onChange(flatOptions);
      }
    }
  };

  return (
    <components.Menu {...props} isMulti={isMulti} options={options}>
      {isMulti && (
        <div
          role="button"
          onClick={handleSelectAll}
          className="d-flex p-2 ms-1"
        >
          <StylishNewCheckbox
            checked={isAllSelected}
            className="planning-checkbox"
          />
          Select All
        </div>
      )}
      {props.children}
    </components.Menu>
  );
};

const Option = (props: OptionProps<OptionType>) => {
  return (
    <components.Option {...props}>
      <StylishNewCheckbox
        checked={props.isSelected}
        className="planning-checkbox"
      />
      {props.label}
    </components.Option>
  );
};

const MultiValueContainer = ({
  data,
  selectProps,
  ...props
}: MultiValueGenericProps<OptionType, boolean, GroupBase<OptionType>>) => {
  const selectedOptions = selectProps.value as OptionType[];
  const isLastValue =
    selectedOptions[selectedOptions.length - 1].value === data.value;

  return (
    <components.MultiValueContainer
      data={data}
      selectProps={selectProps}
      {...props}
    >
      {data.label}
      {!isLastValue && ', '}
    </components.MultiValueContainer>
  );
};

export type PratusSelectProps = SelectProps<
  OptionType,
  boolean,
  GroupBase<OptionType>
> & {
  label?: string;
  classes?: string;
  style?: React.CSSProperties;
  onChange?: (value: OptionType | OptionType[]) => void;
  value?: OptionType | OptionType[] | null;
  options: OptionsOrGroups<OptionType, GroupBase<OptionType>>;
};

const PratusSelect: React.FC<PratusSelectProps> = ({
  label,
  classes,
  style,
  options,
  defaultValue,
  value,
  onChange,
  isMulti,
  ...props
}) => {
  return (
    <div className={classNames('form-selectbox', classes)} style={style}>
      {label && (
        <label className="mb-2" htmlFor={props.inputId}>
          {label}
        </label>
      )}
      <Select
        inputId={props.inputId}
        className="react-select-container"
        classNamePrefix="react-select"
        options={options}
        defaultValue={defaultValue}
        value={value}
        onChange={onChange}
        closeMenuOnSelect={!isMulti}
        components={{
          ...(isMulti && {
            Option,
          }),
          Menu: (menuProps) => (
            <Menu
              {...menuProps}
              value={value}
              onChange={onChange}
              isMulti={isMulti || false}
              options={options}
            />
          ),
          MultiValueContainer,
        }}
        hideSelectedOptions={false}
        isMulti={isMulti}
        noOptionsMessage={() => 'No Items Found'}
        isDisabled={props.isDisabled}
        {...props}
      />
    </div>
  );
};

export default PratusSelect;
